import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { PathElementNode } from "./PathElementNode";
import { PathElementNodeMoveto } from "./PathElementNodeMoveto";
import { PathElementNodeLineto } from "./PathElementNodeLineto";
import { PathElementNodeCurvetoQuadratic } from "./PathElementNodeCurvetoQuadratic";
import { PathElementNodeCurvetoBezier } from "./PathElementNodeCurvetoBezier";
import { PathElementNodeArcto } from "./PathElementNodeArcto";
import { PathElementNodeClosepath } from "./PathElementNodeClosepath";
import { Tweens } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";

export interface PathElementNodeGroup {
    element: PathElementNode;
    index: number;
    absolute: boolean;
}

@engineComponent(ComponentTypes.DisplayObject)
export class PathElement extends ShapeElement {
    private _path: Array<PathElementNode>;

    private _pathTweenArray: Array<Array<PathElementNode>>;
    private _pathTweenFrom: Array<PathElementNode>;
    private _pathTweenTo: Array<PathElementNode>;
    private _pathTweenIndex: number;
    private _pathTweenLastIndex: number;

    constructor() {
        super();
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
        this._path = null;
        this._pathTweenArray = null;
        this._pathTweenFrom = null;
        this._pathTweenTo = null;
    }

    public updateByNode(node: Element): boolean {
        let redraw: boolean = false;

        if (node.hasAttribute(PathElementAttributes.POSITION_X)) {
            this.position.x = parseInt(node.getAttribute(PathElementAttributes.POSITION_X));
        }

        if (node.hasAttribute(PathElementAttributes.POSITION_Y)) {
            this.position.y = parseInt(node.getAttribute(PathElementAttributes.POSITION_Y));
        }

        if (node.hasAttribute(PathElementAttributes.PATH)) {
            this._path = this.parsePath(node.getAttribute(PathElementAttributes.PATH));
            redraw = true;
        }

        this.runPathTween(node);

        if (!super.updateByNode(node)) {
            if (redraw) {
                this.redraw();
                return true;
            } else {
                return false;
            }
        }
        return true;
    }

    private parsePath(input: string): Array<PathElementNode> {
        input = input.replace(/\s+/g, " ").replace(/^\s+|\s+$/, "");
        let pathArray: Array<string> = input.split(" ");
        let pathData: Array<PathElementNode> = [];

        let lastNode: PathElementNode;
        let lastMoveto: PathElementNodeMoveto;
        let lastWasAbsolute: boolean = true;

        for (let index = 0; index < pathArray.length; null) {
            let out = this.parseNode(pathArray, index, lastNode, lastMoveto, lastWasAbsolute);
            if (!out || !out.element) {
                this.logError(
                    'Error parsing path: "' + input + '" close to "' + pathArray.slice(index).join(" ") + '"',
                );
                break;
            }
            lastNode = out.element;
            lastWasAbsolute = out.absolute;
            if (out.element instanceof PathElementNodeMoveto && out.element.command == "M") {
                lastMoveto = out.element;
            }
            pathData.push(lastNode);
            index = out.index;
        }

        return pathData;
    }

    private parseNode(
        array: Array<string>,
        index: number,
        lastPoint: PathElementNode = null,
        lastMoveto: PathElementNodeMoveto = null,
        lastWasAbsolute: boolean = true,
    ): PathElementNodeGroup {
        if (array.length <= index) {
            return null;
        }
        let command: string = /[mMlLcCqQaAzZ]/.test(array[index][0]) ? array[index][0] : null;
        let noCommand: boolean = command == null;
        let relative: boolean;
        if (!command) {
            index--;
            if (!lastPoint) {
                command = "M";
            } else {
                switch (lastPoint.command) {
                    case "M":
                        command = "L";
                        break;
                    default:
                        command = lastPoint.command;
                }
                relative = !lastWasAbsolute;
            }
        } else {
            relative = command == command.toLowerCase();
        }
        let concat: boolean = false;
        if (!noCommand && array[index].length > 1) {
            // concat shorthand, e.g. "C100,20"
            concat = true;
        } else {
            index++;
        }

        let out: PathElementNodeGroup = {
            element: null,
            index: null,
            absolute: !relative,
        };

        switch (command) {
            case "M":
            case "m":
                if (array.length < index) {
                    return null;
                }
                let m1 = this.parsePair(concat ? array[index].substr(1) : array[index]);
                out.element = new PathElementNodeMoveto(
                    (relative && lastPoint != null ? lastPoint.x : 0) + m1.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + m1.y,
                );
                out.index = index + 1;
                break;
            case "L":
            case "l":
                if (array.length < index) {
                    return null;
                }
                let l1 = this.parsePair(concat ? array[index].substr(1) : array[index]);
                out.element = new PathElementNodeLineto(
                    (relative && lastPoint != null ? lastPoint.x : 0) + l1.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + l1.y,
                );
                out.index = index + 1;
                break;

            case "Q":
            case "q":
                if (array.length + 1 < index) {
                    return null;
                }
                let q1 = this.parsePair(concat ? array[index].substr(1) : array[index]);
                let q2 = this.parsePair(array[index + 1]);
                out.element = new PathElementNodeCurvetoQuadratic(
                    (relative && lastPoint != null ? lastPoint.x : 0) + q1.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + q1.y,
                    (relative && lastPoint != null ? lastPoint.x : 0) + q2.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + q2.y,
                );
                out.index = index + 2;
                break;

            case "C":
            case "c":
                if (array.length + 2 < index) {
                    return null;
                }
                let c1 = this.parsePair(concat ? array[index].substr(1) : array[index]);
                let c2 = this.parsePair(array[index + 1]);
                let c3 = this.parsePair(array[index + 2]);
                out.element = new PathElementNodeCurvetoBezier(
                    (relative && lastPoint != null ? lastPoint.x : 0) + c1.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + c1.y,
                    (relative && lastPoint != null ? lastPoint.x : 0) + c2.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + c2.y,
                    (relative && lastPoint != null ? lastPoint.x : 0) + c3.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + c3.y,
                );
                out.index = index + 3;
                break;

            case "A":
            case "a":
                if (array.length + 2 < index) {
                    return null;
                }
                let a1 = this.parsePair(concat ? array[index].substr(1) : array[index]);
                let a2 = this.parsePair(array[index + 1]);
                let a3 = parseFloat(array[index + 2]);
                out.element = new PathElementNodeArcto(
                    (relative && lastPoint != null ? lastPoint.x : 0) + a1.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + a1.y,
                    (relative && lastPoint != null ? lastPoint.x : 0) + a2.x,
                    (relative && lastPoint != null ? lastPoint.y : 0) + a2.y,
                    a3,
                );
                out.index = index + 3;
                break;

            case "Z":
            case "z":
                if (lastMoveto) {
                    out.element = new PathElementNodeClosepath(lastMoveto.x, lastMoveto.y);
                    out.index = index + 1;
                }

                break;
        }
        return out;
    }

    private parsePair(pair: string): PIXI.Point {
        let arr = pair.split(",").map((value) => parseFloat(value));
        return new PIXI.Point(arr.length > 0 ? arr[0] : null, arr.length > 1 ? arr[1] : null);
    }

    private runPathTween(node: Element): void {
        if (!node.hasAttribute(PathElementAttributes.PATH_TWEEN)) {
            return;
        }

        Tweens.removeTweens(this, "pathIndex");

        this._pathTweenLastIndex = null;

        let tweenValues: Array<number> = [];

        let lastPath: Object;

        this._pathTweenArray = node
            .getAttribute(PathElementAttributes.PATH_TWEEN)
            .split(";")
            .map((value, index) => {
                let out: Array<PathElementNode>;
                if (
                    index == 0 &&
                    node.getAttribute(PathElementAttributes.PATH_TWEEN) ==
                        PathElementAttributes.PATH_TWEEN_POSITION_VALUE.SMOOTH
                ) {
                    out = [];
                    this._path.forEach((node) => {
                        out.push(node.clone());
                    });
                } else {
                    out = this.parsePath(value);
                }
                if (!lastPath) {
                    lastPath = {};
                }
                let anyChange: boolean = false;
                out.forEach((value, index) => {
                    if (lastPath[index] && lastPath[index].equalTo(value)) {
                        out[index] = null;
                    } else {
                        lastPath[index] = value;
                        anyChange = true;
                    }
                });
                if (!anyChange) {
                    out = null;
                    tweenValues.push(tweenValues.length ? tweenValues[tweenValues.length - 1] : 0);
                } else {
                    if (tweenValues.length) {
                        tweenValues[tweenValues.length - 1] = tweenValues.length - 1;
                    }
                    tweenValues.push(tweenValues.length);
                }
                return out;
            });

        const tween: string = "tweenIndex";
        const tweenEase: Array<string> = ArrayUtils.stringArray(
            node.getAttribute(PathElementAttributes.PATH_TWEEN_EASE) || "linear",
        );
        const tweenDuration: Array<number> = [
            Attributes.assignIntAttribute(node, PathElementAttributes.PATH_TWEEN_DURATION, 120),
        ];
        let tweenDelay: Array<number> = [
            Attributes.assignIntAttribute(node, PathElementAttributes.PATH_TWEEN_DELAY, 0),
        ];
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(node, PathElementAttributes.PATH_TWEEN_LOOP, null);
        const keyTimes: Array<number> = node.hasAttribute(PathElementAttributes.PATH_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(PathElementAttributes.PATH_TWEEN_KEY_TIMES))
            : null;

        Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    get tweenIndex(): number {
        return this._pathTweenIndex;
    }

    set tweenIndex(value: number) {
        this._pathTweenIndex = value;
        let index = Math.floor(this._pathTweenIndex);
        let ratio = this._pathTweenIndex - index;
        if (index >= this._pathTweenArray.length - 1) {
            index = this._pathTweenArray.length - 2;
            ratio = 1;
        }
        if (index != this._pathTweenLastIndex) {
            this._pathTweenLastIndex = index;
            if (this._pathTweenArray[index]) {
                this._pathTweenFrom = this._pathTweenArray[index];
            }
            if (this._pathTweenArray[index + 1]) {
                this._pathTweenTo = this._pathTweenArray[index + 1] ? this._pathTweenArray[index + 1] : null;
            }
        }
        let redraw: boolean = false;
        if (!this._pathTweenFrom || !this._pathTweenTo) {
            return;
        }
        this._path.forEach((node, i) => {
            if (this._pathTweenFrom[i] != null && this._pathTweenTo[i] != null) {
                redraw = true;
                node.setInterpolatedTo(this._pathTweenFrom[i], this._pathTweenTo[i], ratio);
            }
        });
        if (redraw) {
            this.redraw();
        }
    }

    public redraw(): void {
        this.clear();
        this.beginFill(this._color, this._fillAlpha);
        this.lineStyle(this._lineWidth, this._lineColor, this._lineAlpha);

        if (this._path) {
            this._path.forEach((node) => {
                node.draw(this);
            });
        }

        this.endFill();
    }

    clone(): PathElement {
        let element: PathElement = new PathElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;
        element._tweenEngine = this._tweenEngine;

        element._lineWidth = this._lineWidth;
        element._lineColor = this._lineColor;
        element._lineAlpha = this._lineAlpha;
        element._fillAlpha = this._fillAlpha;
        element._color = this._color;
        element._path = [];

        this._path.forEach((node) => {
            element._path.push(node.clone());
        });

        element.redraw();

        return element;
    }
}

export const PathElementAttributes = {
    /*
     * SVG-like path attribute, e.g. "M 100,200 L 200,300 C 100,200 300,400 150,200 Z"
     */
    PATH: "Path",

    PATH_TWEEN: "PathTween",
    PATH_TWEEN_EASE: "PathTweenEase",
    PATH_TWEEN_DURATION: "PathTweenDuration",
    PATH_TWEEN_DELAY: "PathTweenDelay",
    PATH_TWEEN_POSITION: "PathTweenPath",
    PATH_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        SMOOTH: "smooth",
    },
    PATH_TWEEN_LOOP: "PathTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    PATH_TWEEN_KEY_TIMES: "PathTweenKeyTimes",

    POSITION_X: "PositionX",
    POSITION_Y: "PositionY",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/PathElement.ts
 **/